import { AREA_TYPE } from '@/constants/area-constants';
import { appendFilter } from '@/utilities/filters';
import { Deserializer } from 'jsonapi-serializer';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { AccessArea } from '../models/AccessArea';
import { Door } from '../models/Door';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { DeleteEntityResult } from '../models/services/DeleteEntityResult';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { AccessAreaWrapper } from '../models/wrappers/AccessAreaWrapper';
import { APIConnector } from './api/APIConnector';
import { getConstraintViolation, processServerError } from './utilities/ServiceError';
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();

export const deleteAccessArea = async (id: string): Promise<DeleteEntityResult> => {
    //first delete any areaLocations linked to this area
    try {
        const url = `area/${id}`;
        const accessArea = {
            data: {
                type: "area",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, accessArea)
        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeAccessArea = async (accessArea: AccessArea, createAccessArea = true) => {
    try {
        const newRecord = {
            data: {
                type: 'area',
                id: accessArea.id,
                attributes: {
                    areaDefault: accessArea.default ?? 0,
                    areaDesc: accessArea.description,
                    areaName: accessArea.name,
                    areaType: accessArea.areaType,
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: accessArea.siteId
                        }
                    }
                }
            }
        };

        const url = `area${createAccessArea ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createAccessArea, newRecord)
            .then(async data => {
                response = data;

                if (accessArea.locationIdsToRemove?.length ?? 0 > 0) {
                    await deleteAreaLocations(
                        accessArea.locationIdsToRemove ?? []
                    );
                }

                if (accessArea.doorIds && accessArea.doorIds.length  > 0) {
                    //create new areaLocations
                    const responses = await Promise.all(accessArea.doorIds.map(doorId => storeAreaLocation(accessArea.id, doorId)));
                }
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createAccessArea) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newAccessArea) => {
                        if (error) {
                            return reject({status: response.status, id: accessArea.id, success: false });
                        }
                        return resolve({ id: newAccessArea.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: accessArea.id, success: true })
                }

                const content = await response.json();
                return reject({status: response.status, id: accessArea.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({status: INTERNAL_SERVER_ERROR, id: accessArea.id, success: false, message: genericError });
        })
        
    } catch (error) {
    
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeAreaLocation = async (areaId, locationId) => {
    const url = `areaLocation`;
    const newAreaLocation = {
        data: {
            type: "areaLocation",
            id: 0,
            relationships: {
                area: {
                    data: {
                        type: "area",
                        id: areaId
                    }
                },
                location: {
                    data: {
                        type: "location",
                        id: locationId
                    }
                }
            }
        }
    }
    return apiConnector.sendData(url, true, newAreaLocation);
}

export const storeAreaLocationData = async (areaId: number, locationId: number): Promise<{ id?: string, status: string, message: string }> => {
    try {
        const locData = await storeAreaLocation(areaId, locationId);
        const json = await locData.json();
        
        return new Promise((resolve, reject) => {
            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (err) {
                    reject(err);
                } else {
                    resolve({...data,status: locData.status, message:locData.statusText});
                }
            });
        });
    } catch (error) {
        return Promise.resolve(processServerError(error));
    }
};

export const deleteAreaLocations = async (ids: number[]) => {
    const baseUrl = 'areaLocation';
    return Promise.all(ids.map(id => {
        const currentAreaLocation = {
            data: {
                type: "areaLocation",
                id: id
            }
        }
        return apiConnector.deleteData(`${baseUrl}/${id}`, currentAreaLocation);
    }))
}

// Assuming apiConnector.deleteData is defined elsewhere and handles HTTP DELETE requests.
export const deleteAreaLocation = async (id: number): Promise<Response> => {
    const baseUrl = 'areaLocation';
    // Construct the payload for the DELETE request
    const currentAreaLocation = {
        data: {
            type: "areaLocation",
            id: id
        }
    };
    // Make a DELETE request to the specified URL with the area location data
    return apiConnector.deleteData(`${baseUrl}/${id}`, currentAreaLocation);
}

interface AreaLocation {
    id: number,
    areaId: number,
    doorId: number,
}

export const getAreaLocations = async (listQuery?: ListQuery): Promise<AreaLocation[]> => {
    try {
        const filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const partialUrl = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filter)
        const include = "include=area.site,location";
        const areaLocationFields = "fields[areaLocation]=area,location";
        const areaFields = "fields[area]=areaName,totalLocations,site";
        const siteFields = "fields[site]=siteName";
        const doorFields = "fields[location]=locName";
        const url = `areaLocation?${include}&${areaLocationFields}&${areaFields}&${siteFields}&${doorFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAreaLocations: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const areaLocations = apiAreaLocations.map(apiAreaLocation => {
                    return {
                        id: apiAreaLocation['id'],
                        areaId: apiAreaLocation['area']['id'],
                        doorId: apiAreaLocation['location']['id'],
                    }
                })
                return resolve(areaLocations);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDoorsByAccessArea = async (id?: string): Promise<any> => {
    try {
        const include = "include=include=area.site,location.zoneByZoneId.controller.deviceImprox";
        const areaLocationFields = "fields[areaLocation]=area,location";
        const siteFields = "fields[site]=siteName";
        const areaFields = "fields[area]=areaName,totalLocations,site";
        const controllerFields = "fields[controller]=devName";
        const zoneFields = "fields[zone]=zoneName,controller";
        const doorFields = "fields[location]=locName,zoneByZoneId";
        const filter = "${id ? `&filter=area.id==${id}` : ''}";
        const url = `areaLocation?${include}&${areaLocationFields}${siteFields}&${areaFields}&${controllerFields}&${zoneFields}${doorFields}${filter}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAreaLocations: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const doors: Door[] = apiAreaLocations.map((apiAreaLocation) => {
                    return {
                        id: apiAreaLocation['location']['id'],
                        name: apiAreaLocation['location']['loc-name'],
                        zoneId: apiAreaLocation['location']['zone-by-zone-id']['id'],
                        zone: apiAreaLocation['location']['zone-by-zone-id']['zone-name'],
                        controller: apiAreaLocation['location']['zone-by-zone-id']['controller']['device-improx']['dev-name'],
                        controllerId: apiAreaLocation['location']['zone-by-zone-id']['controller']['device-improx']['id'],
                        areaId: apiAreaLocation['area']['id'],
                    }
                })

                return resolve(doors);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessAreaById = async (id: string): Promise<AccessArea> => {
    try {
        const areaFields = "fields[area]=areaName,areaDesc,areaDefault,totalLocations,totalAccessGroups,totalFloorGroups,site,isDefault";
        const siteFields = "fields[site]=siteName";
        const url = `area/${id}?include=site&${areaFields}&${siteFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessArea: any) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const accessArea = buildAccessArea(apiAccessArea);
                return resolve(accessArea);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessAreaNamesWithPagination = async (areaType: AREA_TYPE = null, listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filtersWithAreaType = areaType !== null ? appendFilter(areaType, "Area Type", "areaType", listQuery?.filters ?? []) : listQuery?.filters;
        const filters: string = getFilterClause(filtersWithAreaType, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const areaFields = 'fields[area]=areaName,isDefault,areaType,site';
        const siteFields = 'fields[site]=siteName';
        const url = `area?${paginationString}&include=site&${areaFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessAreas: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const accessAreas: Name[] = apiAccessAreas.map((area): Name => {
                    return {
                        value: area['id'],
                        label: area['area-name'],
                        optionalColumns: [{ key: 'site', value: area['site']?.['site-name'] }],
                        isDefault: area['is-default']
                    };
                });

                return resolve({ rows: accessAreas, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessAreas = async (areaType: AREA_TYPE = null, listParameters?: ListQuery): Promise<AccessAreaWrapper> => {
    try {
        const filter: string = getFilterClause(appendFilter(areaType, "Area Type", "areaType", listParameters?.filters ?? []), listParameters?.searchTerm);
        const fieldsArea = '&fields[area]=areaName,isDefault,totalLocations,site,areaType,liftDoor';
        const fieldsSite = '&fields[site]=siteName';
        const url = 'area?include=site' + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + fieldsArea + fieldsSite;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessAreas: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const accessAreas: AccessArea[] = apiAccessAreas.map(buildAccessArea);

                return resolve({ rows: accessAreas, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildAccessArea = (area: any): AccessArea => {
    return {
        id: area['id'],
        name: area['area-name'],
        site: area['site']['site-name'],
        doors: area['total-locations'],
        liftDoor: area['lift-door'],
        description: area['area-desc'] ?? '',
        siteId: area['site']['id'],
        default: area['area-default'],
        accessGroups: area['total-access-groups'],
        floorGroups: area['total-floor-groups'],
        isDefault: area['is-default']
    };
};

export const getAccessAreaTabCounts = async (areaId: string): Promise<{ accessGroupCount: number, floorGroupCount: number }> => {
    try {
        const areaFields = 'fields[area]=totalAccessGroups,totalFloorGroups';
        const url = `area/${areaId}?${areaFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, data: any) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                resolve({
                    accessGroupCount: data['total-access-groups'],
                    floorGroupCount: data['total-floor-groups']
                });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
